import {
  getTimeZoneOffsetMinutes,
  getTimeZoneSuffix,
  parseUtcTimestampToMs,
} from "../../Utils/timezone";

const DATE_CAPTURE = /^(\d{4}-\d{2}-\d{2})/;
const DATE_TIME_INPUT_CAPTURE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

const padDatePart = (value: number) => String(value).padStart(2, "0");

const MINUTE_MS = 60000;

// "YYYY-MM-DDTHH:mm" from a Date's UTC fields.
const toUtcFieldsInputValue = (date: Date) =>
  `${date.getUTCFullYear()}-${padDatePart(date.getUTCMonth() + 1)}-${padDatePart(
    date.getUTCDate()
  )}T${padDatePart(date.getUTCHours())}:${padDatePart(date.getUTCMinutes())}`;

const toWallClockInputValue = (timestampMs: number, timeZone: string) =>
  toUtcFieldsInputValue(
    new Date(
      timestampMs + getTimeZoneOffsetMinutes(timeZone, timestampMs) * MINUTE_MS
    )
  );

const toUtcDateTimeString = (timestampMs: number) =>
  `${toUtcFieldsInputValue(new Date(timestampMs)).replace("T", " ")}:00`;

/**
 * Any parseable UTC timestamp -> "YYYY-MM-DD HH:mm:00", the format requests
 * and storage expect.
 */
export const toUtcDateTimeValue = (value: string | null | undefined) => {
  const timestampMs = parseUtcTimestampToMs(value);
  return Number.isFinite(timestampMs) ? toUtcDateTimeString(timestampMs) : "";
};

/**
 * UTC timestamp ("YYYY-MM-DD HH:mm:ss") -> `datetime-local` value on
 * `timeZone`'s wall clock. Form state stays in UTC; only the input is zoned.
 */
export const toZonedDateTimeInputValue = (
  value: string | null | undefined,
  timeZone: string
) => {
  const timestampMs = parseUtcTimestampToMs(value);
  return Number.isFinite(timestampMs)
    ? toWallClockInputValue(timestampMs, timeZone)
    : "";
};

/**
 * `datetime-local` value on `timeZone`'s wall clock -> UTC
 * "YYYY-MM-DD HH:mm:00".
 */
export const fromZonedDateTimeInputValue = (
  value: string | null | undefined,
  timeZone: string
) => {
  const match = String(value ?? "").trim().match(DATE_TIME_INPUT_CAPTURE);
  if (!match) return "";

  const [, year, month, day, hour, minute] = match.map(Number);
  const wallClockMs = Date.UTC(year, month - 1, day, hour, minute);
  // The offset depends on the instant being solved for; a second pass settles
  // wall-clock times near a DST transition.
  const firstGuessMs =
    wallClockMs - getTimeZoneOffsetMinutes(timeZone, wallClockMs) * MINUTE_MS;
  const timestampMs =
    wallClockMs - getTimeZoneOffsetMinutes(timeZone, firstGuessMs) * MINUTE_MS;

  return toUtcDateTimeString(timestampMs);
};

export const toDateInputValue = (value?: string | null) => {
  if (!value) return "";

  const match = value.trim().match(DATE_CAPTURE);
  return match ? match[1] : "";
};

export const fromDateInputValue = (value?: string | null) => {
  if (!value) return "";

  const trimmed = value.trim();
  const match = trimmed.match(/^\d{4}-\d{2}-\d{2}$/);
  return match ? trimmed : "";
};

/** Zone name for a date-time field's label, at the entered instant (EST/EDT). */
export const getDateTimeInputZoneLabel = (
  value: string | null | undefined,
  timeZone: string
) => getTimeZoneSuffix(timeZone, parseUtcTimestampToMs(value));

/**
 * Earliest allowed time up to now, as `datetime-local` bounds in `timeZone`.
 * The earliest time defaults to two months ago.
 */
export const getExecutionTimestampBounds = (
  timeZone: string,
  earliestTimestampMs?: number
) => {
  const now = new Date();
  now.setUTCSeconds(0, 0);

  const twoMonthsAgo = new Date(now);
  twoMonthsAgo.setUTCMonth(twoMonthsAgo.getUTCMonth() - 2);

  return {
    minExecutionTimestamp: toWallClockInputValue(
      earliestTimestampMs ?? twoMonthsAgo.getTime(),
      timeZone
    ),
    maxExecutionTimestamp: toWallClockInputValue(now.getTime(), timeZone),
  };
};
